/**
 * DashasSection - Planetary Periods (Dasha Systems)
 *
 * Structure (like Chart Info -> Time -> Movement):
 * - Dashas (main accordion section)
 *   - Vimsottari (expandable subsection)
 *     - Periods table
 *   - Future: Yogini, Ashtottari, etc.
 */

import React, { useState, useCallback, useMemo, useRef } from 'react';
import { Box, Text } from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import { AccordionSection } from '../AccordionSection';
import { AccordionItem } from '../types';
import { ExpandableLabel } from '../ExpandableLabel';
import { VimsottariDashaTable } from '../components/dashas/VimsottariDashaTable';
import { useSectionState } from '../hooks/useSectionState';
import { useMultipleCharts } from '../../MultipleChartsContext';

export interface DashasSectionProps {
  apiResponse?: JatakaResponse | null;
  className?: string;
}

/**
 * Extract data for dashas section
 */
function extractDashasData(response: JatakaResponse): JatakaResponse {
  return response;
}

/**
 * Map data to accordion items
 */
function generateDashasItems(data: JatakaResponse): AccordionItem[] {
  return [{
    id: 'dashas-main',
    name: 'Dashas',
    short_name: 'DA',
    type: 'dashas',
    details: []
  }];
}

/**
 * VimsottariExpander - Expandable Vimsottari subsection
 * Similar to TimeHistoryExpander pattern
 */
interface VimsottariExpanderProps {
  apiResponse: JatakaResponse;
  chartId: string;
}

const VimsottariExpander: React.FC<VimsottariExpanderProps> = ({
  apiResponse,
  chartId
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="planet-detail-expandable-container">
      <div className="planet-detail-row">
        <ExpandableLabel
          label="Vimsottari (120 years)"
          isExpanded={isExpanded}
          onToggle={handleToggle}
          ariaControls={`vimsottari-content-${chartId}`}
        />
      </div>

      {isExpanded && (
        <div
          id={`vimsottari-content-${chartId}`}
          className="planet-detail-subsection"
        >
          <VimsottariDashaTable apiResponse={apiResponse} />
        </div>
      )}
    </div>
  );
};

/**
 * DashasContent - Content inside Dashas section
 * Contains expandable dasha subsections
 */
interface DashasContentProps {
  apiResponse: JatakaResponse;
  chartId: string;
}

const DashasContent: React.FC<DashasContentProps> = ({
  apiResponse,
  chartId
}) => {
  return (
    <div className="planet-detail-container">
      <div className="planet-detail-content">
        {/* Vimsottari subsection */}
        <VimsottariExpander
          apiResponse={apiResponse}
          chartId={chartId}
        />

        {/* Future: Other dasha systems */}
        {/* <YoginiExpander apiResponse={apiResponse} chartId={chartId} /> */}
        {/* <AshtottariExpander apiResponse={apiResponse} chartId={chartId} /> */}
      </div>
    </div>
  );
};

/**
 * DashasSection Component
 */
export const DashasSection: React.FC<DashasSectionProps> = ({
  apiResponse,
  className
}) => {
  const { activeChartId } = useMultipleCharts();

  // Section state persistence
  const storageKey = activeChartId || 'default';
  const { loadAccordionState, saveAccordionState, isLoading: isStateLoading, state } = useSectionState(
    storageKey,
    'dashas'
  );

  // Default: section collapsed
  const DEFAULT_SECTIONS: string[] = [];
  const initializedRef = useRef(false);

  // Initialize state with defaults (only once)
  React.useEffect(() => {
    if (isStateLoading || !storageKey || initializedRef.current) return;

    if (!state?.hasInteracted) {
      const savedSections = Array.from(loadAccordionState());
      if (savedSections.length === 0) {
        initializedRef.current = true;
        saveAccordionState(new Set(DEFAULT_SECTIONS));
      }
    }
  }, [isStateLoading, state, storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load expanded sections
  const defaultExpandedSections = useMemo(() => {
    if (isStateLoading) return null;

    const savedSections = Array.from(loadAccordionState());

    if (!state?.hasInteracted && savedSections.length === 0) {
      return DEFAULT_SECTIONS;
    }

    return savedSections;
  }, [isStateLoading, loadAccordionState, state]);

  // Handle accordion change
  const handleAccordionChange = useCallback((expandedSections: Set<string>) => {
    saveAccordionState(expandedSections);
  }, [saveAccordionState]);

  // Safety checks
  if (!apiResponse) {
    return (
      <div className={className} style={{ padding: '16px', color: '#666' }}>
        No chart data available
      </div>
    );
  }

  if (isStateLoading) {
    return (
      <div className={className} style={{ padding: '16px', color: '#666' }}>
        Loading...
      </div>
    );
  }

  return (
    <AccordionSection
      dataExtractor={extractDashasData}
      itemsMapper={generateDashasItems}
      defaultExpandedSections={defaultExpandedSections || []}
      loadingMessage="Loading Dashas..."
      idPrefix="dashas"
      apiResponse={apiResponse}
      className={className}
      onAccordionChange={handleAccordionChange}
      customDetailRenderer={(item: AccordionItem) => {
        if (item.type === 'dashas') {
          return (
            <DashasContent
              apiResponse={apiResponse}
              chartId={activeChartId || 'default'}
            />
          );
        }

        return (
          <Box p={4}>
            <Text color="gray.500">Unknown section type</Text>
          </Box>
        );
      }}
    />
  );
};

export default DashasSection;
